import React from "react";
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import {
  FaBuilding,
  FaHome,
  FaUsers,
  FaBriefcase,
  FaLightbulb,
  FaHeartbeat,
  FaEnvelope,
  FaPhone,
  FaClock,
} from "react-icons/fa";
import "../PracticeArea/PracticeAreaDetail.css";

const practiceAreas = [
  {
    id: "corporate-securities",
    title: "Corporate & Securities",
    description:
      "Mergers & acquisitions, business formation, compliance, securities regulations",
    icon: <FaBuilding className="detail-icon" />,
    image: "/demo.webp",
    content: {
      overview:
        "Our Corporate & Securities practice provides comprehensive legal services for businesses of all sizes. We specialize in mergers and acquisitions, securities offerings, corporate governance, and regulatory compliance.",
      process: [
        {
          title: "Initial Consultation",
          description:
            "We begin with a thorough assessment of your corporate needs and objectives.",
        },
        {
          title: "Strategic Planning",
          description:
            "Our team develops a customized strategy tailored to your business goals.",
        },
        {
          title: "Implementation",
          description:
            "We handle all legal aspects of your corporate transactions with precision.",
        },
        {
          title: "Ongoing Support",
          description:
            "We provide continuous legal support to ensure compliance and address emerging issues.",
        },
      ],
    },
  },
  {
    id: "real-estate",
    title: "Real Estate Law",
    description: "Property transactions, zoning issues, landlord disputes",
    icon: <FaHome className="detail-icon" />,
    image: "/realestate.webp",
    content: {
      overview:
        "Our Real Estate Law practice handles all aspects of property transactions, from residential purchases to complex commercial developments. We navigate zoning laws, landlord-tenant disputes, and property financing with expertise.",
      process: [
        {
          title: "Property Evaluation",
          description:
            "Comprehensive assessment of your real estate assets and legal standing.",
        },
        {
          title: "Document Preparation",
          description:
            "Drafting and reviewing all necessary contracts and agreements.",
        },
        {
          title: "Transaction Management",
          description:
            "Overseeing the entire buying/selling process to ensure legal compliance.",
        },
        {
          title: "Dispute Resolution",
          description:
            "Representing clients in property-related conflicts and negotiations.",
        },
      ],
    },
  },
  {
    id: "healthcare",
    title: "Healthcare Law",
    description: "Medical malpractice, patient rights, regulatory compliance",
    icon: <FaHeartbeat className="detail-icon" />,
    image: "/health.webp",
    content: {
      overview:
        "Our Healthcare Law division provides legal guidance to healthcare providers, patients, and institutions. We ensure compliance with medical regulations, protect patient rights, and represent clients in malpractice and insurance disputes.",
      process: [
        {
          title: "Regulatory Review",
          description:
            "Analyzing and ensuring compliance with healthcare laws and standards.",
        },
        {
          title: "Malpractice Defense",
          description:
            "Defending healthcare providers in cases of alleged medical negligence.",
        },
        {
          title: "Patient Advocacy",
          description:
            "Protecting the rights and interests of patients through legal support.",
        },
        {
          title: "Insurance Disputes",
          description:
            "Resolving conflicts between healthcare providers and insurance companies.",
        },
      ],
    },
  },
  {
    id: "family",
    title: "Family Law",
    description: "Divorce, child custody, adoption, spousal support",
    icon: <FaUsers className="detail-icon" />,
    image: "/family.webp",
    content: {
      overview:
        "Our Family Law services offer compassionate and strategic support in sensitive matters like divorce, custody battles, adoption, and spousal support. We prioritize the well-being of families while protecting your legal interests.",
      process: [
        {
          title: "Initial Consultation",
          description:
            "Understanding your unique family situation and legal needs.",
        },
        {
          title: "Custody Planning",
          description:
            "Developing arrangements that serve the best interests of children.",
        },
        {
          title: "Asset Division",
          description:
            "Ensuring equitable distribution of property and financial assets.",
        },
        {
          title: "Legal Representation",
          description:
            "Advocating for your rights in court and during negotiations.",
        },
      ],
    },
  },
  {
    id: "intellectual-property",
    title: "Intellectual Property",
    description: "Copyright, trademarks, patents, trade secrets",
    icon: <FaLightbulb className="detail-icon" />,
    image: "/property.webp",
    content: {
      overview:
        "Our Intellectual Property Law services help protect your innovations, brand identity, and creative works. From registering patents and trademarks to defending your rights against infringement, we ensure your intellectual assets are secured.",
      process: [
        {
          title: "IP Assessment",
          description:
            "Evaluating your intellectual assets and determining the best protection strategy.",
        },
        {
          title: "Registration Services",
          description:
            "Assisting with the application and filing of trademarks, copyrights, and patents.",
        },
        {
          title: "Licensing Agreements",
          description:
            "Drafting and reviewing contracts that allow others to use your IP legally.",
        },
        {
          title: "Enforcement & Defense",
          description:
            "Taking legal action against infringement and defending your IP rights.",
        },
      ],
    },
  },
  {
    id: "employment",
    title: "Employment Law",
    description:
      "Workplace rights, wrongful termination, discrimination claims",
    icon: <FaBriefcase className="detail-icon" />,
    image: "/employ.webp",
    content: {
      overview:
        "Our Employment Law services advocate for both employees and employers. We handle cases involving wrongful termination, workplace harassment, wage disputes, and discrimination. Our goal is to ensure fair treatment and compliance with labor laws.",
      process: [
        {
          title: "Case Evaluation",
          description:
            "Assessing the merits of your employment-related legal concerns.",
        },
        {
          title: "Contract Review",
          description:
            "Analyzing employment agreements and identifying potential legal risks.",
        },
        {
          title: "Negotiation & Mediation",
          description:
            "Resolving disputes through effective communication and legal strategy.",
        },
        {
          title: "Litigation Support",
          description:
            "Providing representation in court for employment law violations.",
        },
      ],
    },
  },
];

const PracticeAreaDetail = () => {
  const { id } = useParams();
  const practiceArea = practiceAreas.find((area) => area.id === id);

  if (!practiceArea) {
    return <div className="not-found">Practice area not found</div>;
  }

  return (
    <div className="practice-area-container">
      <div className="sidebar">
        <div className="sidebar-content">
          <h2 className="sidebar-title">Practice Areas</h2>
          <ul className="practice-list">
            {practiceAreas.map((area) => (
              <li key={area.id} className={area.id === id ? "active" : ""}>
                <Link to={`/practicearea/${area.id}`}>
                  {area.icon}
                  <span>{area.title}</span>
                </Link>
              </li>
            ))}
          </ul>

          <div className="help-box">
            <h3>Let Us Help!</h3>
            <p>
              Our team of experienced Lawyer Firm is ready to assist you with
              all your legal needs.
            </p>
            <div className="contact-info">
              <div className="contact-item">
                <FaEnvelope className="contact-icon" />
                <span>contact@lawfirm.com</span>
              </div>
              <div className="contact-item">
                <FaPhone className="contact-icon" />
                <span>(123) 456-7890</span>
              </div>
              <div className="contact-item">
                <FaClock className="contact-icon" />
                <span>Mon-Fri: 9AM-5PM</span>
              </div>
            </div>
          </div>
          <div className="siim">
            <img src="/law.webp" className="sim-img" alt="motion" />
          </div>
        </div>
      </div>

      <div className="main-content">
        {/* Rest of your main content remains the same */}
        <div className="header-section">
          <div className="header-text">
            <h1>{practiceArea.title}</h1>
            <p className="description">{practiceArea.description}</p>
            <div className="divider"></div>
            <p className="overview">{practiceArea.content.overview}</p>
          </div>
          <div className="header-image">
            <img src={practiceArea.image} alt={practiceArea.title} />
          </div>
        </div>

        <div className="process-section">
          <h2 className="section-title">Our Process</h2>
          <div className="process-content">
            <div className="process-image">
              <img src="/aiease.webp" alt="Legal process" />
            </div>
            <div className="process-steps">
              {practiceArea.content.process.map((step, index) => (
                <div key={index} className="process-step">
                  <div className="step-number">{index + 1}</div>
                  <div className="step-content">
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PracticeAreaDetail;
